/**
 * A Docker Compose app outside `services/` (vfs-service `GET /runnables` `outside`: an AI agent asked to "create a docker
 * compose" wrote `docker/stack-local.yml`): Flow only runs `services/<name>` folders, so Apps says so in a notice, "Found
 * a Docker Compose app outside services/: docker/stack-local.yml", with its services and **Move to services/<name>**.
 *
 * The move is shown before it is applied (vfs-service plans it: `moveComposeOutside` without `apply`): the file from → to,
 * the folders only the stack uses that move with it, the relative paths rewritten so they still resolve, and a
 * `.flow/service.json` added when the file has no `build:`. Move applies it as one change (git keeps the files' history).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Input, Modal, message as toast } from 'antd';
import { moveComposeOutside } from '../../services/SandboxService';

const NAME = /^[a-z0-9][a-z0-9-]{0,62}$/;

/** `stack`, else `stack-2`, `stack-3`… the first name no service has. */
export function freeName(taken, base = 'stack') {
  const names = new Set(taken);
  if (!names.has(base)) { return base; }
  let n = 2;
  while (names.has(`${base}-${n}`)) { n += 1; }
  return `${base}-${n}`;
}

const KIND_LABELS = { build: 'build', volume: 'volume', env_file: 'env file' };

/** The confirmation: what the move changes. */
function MovePlan({ plan }) {
  return (
    <div className="apps-move-plan" data-testid="apps-move-plan">
      <p className="apps-move-line"><code>{plan.file}</code> → <code>{plan.to}</code></p>
      {plan.moves.length > 0 && (
        <>
          <h4>Moves with it</h4>
          <ul data-testid="apps-move-moves">
            {plan.moves.map((move) => <li key={move.from}><code>{move.from}</code> → <code>{move.to}</code></li>)}
          </ul>
        </>
      )}
      {plan.rewrites.length > 0 && (
        <>
          <h4>Paths rewritten in the compose file</h4>
          <ul data-testid="apps-move-rewrites">
            {plan.rewrites.map((item) => (
              <li key={item.from}>{item.service} · {KIND_LABELS[item.kind] || item.kind}: <code>{item.from}</code> → <code>{item.to}</code></li>
            ))}
          </ul>
        </>
      )}
      {plan.serviceJson && <p className="apps-muted">Adds <code>services/{plan.name}/.flow/service.json</code> so it runs as a Docker Compose app.</p>}
      {plan.moves.length === 0 && plan.rewrites.length === 0 && <p className="apps-muted">No paths to rewrite.</p>}
    </div>
  );
}

MovePlan.propTypes = { plan: PropTypes.object.isRequired };

export default function OutsideCompose({ projectId, found, taken = [], readOnly = false, onMoved = null }) {
  const [name, setName] = useState(() => freeName(taken));
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  if (!found.length) { return null; }
  const valid = NAME.test(name) && !taken.includes(name);

  const review = async(file) => {
    setBusy(file);
    setError(null);
    try {
      setPlan(await moveComposeOutside(projectId, { file, name }));
    } catch (failure) {
      setError(failure.message || `${file} cannot be moved`);
    } finally {
      setBusy(null);
    }
  };
  const apply = async() => {
    setBusy('apply');
    try {
      await moveComposeOutside(projectId, { file: plan.file, name: plan.name, apply: true });
      toast.success(`Moved to services/${plan.name}`);
      setPlan(null);
      onMoved?.(`services/${plan.name}`);
    } catch (failure) {
      toast.error(failure.message || 'The move failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="apps-card apps-outside" data-testid="apps-outside-compose" role="status">
      {found.map((item) => (
        <div className="apps-outside-row" key={item.path}>
          <p className="apps-outside-text">
            Found a Docker Compose app outside services/: <code>{item.path}</code>
            {item.services?.length > 0 && <span className="apps-muted"> ({item.services.map((service) => service.name).join(', ')})</span>}
          </p>
          <p className="apps-muted">Flow runs only services/&lt;name&gt; folders. Move it into a service of its own; its build contexts then point at the sibling services.</p>
          {!readOnly && (
            <div className="apps-outside-actions">
              <Input
                prefix="services/"
                aria-label="Service name"
                data-testid="apps-outside-name"
                onChange={(event) => setName(event.target.value.trim().toLowerCase())}
                size="middle"
                status={valid ? undefined : 'error'}
                style={{ width: 220 }}
                value={name}
              />
              <Button data-testid={`apps-outside-move-${item.path}`} disabled={!valid || Boolean(busy)} loading={busy === item.path} onClick={() => review(item.path)}>
                Move to services/{name}
              </Button>
            </div>
          )}
        </div>
      ))}
      {!valid && <p className="apps-muted" role="alert">{taken.includes(name) ? `services/${name} already exists` : 'Lowercase letters, digits and -'}</p>}
      {error && <p className="apps-outside-error" role="alert">{error}</p>}
      <Modal
        cancelText="Cancel"
        destroyOnHidden
        okButtonProps={{ loading: busy === 'apply', 'data-testid': 'apps-outside-confirm' }}
        okText="Move"
        onCancel={() => setPlan(null)}
        onOk={apply}
        open={Boolean(plan)}
        title={plan ? `Move ${plan.file} to services/${plan.name}?` : ''}
      >
        {plan && <MovePlan plan={plan} />}
      </Modal>
    </section>
  );
}

OutsideCompose.propTypes = {
  // vfs-service's `outside`: [{ path, services }].
  found: PropTypes.arrayOf(PropTypes.object).isRequired,
  onMoved: PropTypes.func,
  projectId: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  // The project's service names (`api`), not offered as the new one.
  taken: PropTypes.arrayOf(PropTypes.string),
};
